import { FC, Fragment } from 'react';
import {
  Button,
  Dropdown,
  InlineLoading,
  InlineNotification,
  StructuredListBody,
  StructuredListCell,
  StructuredListRow,
  StructuredListWrapper,
} from '@carbon/react';
import { formatNumber } from 'utils/number';
import type { MergeEndOption, MergeGeometries } from './use-merge-geometries';
import style from './unit-builder-modal.module.scss';

type MergeGeometriesPanelProps = Pick<
  MergeGeometries,
  | 'draft'
  | 'landingOptions'
  | 'bottomHoleOptions'
  | 'landingKey'
  | 'bottomHoleKey'
  | 'chain'
  | 'creating'
> & {
  onAssign: MergeGeometries['assignEnd'];
  onCancel: () => void;
  onCreate: () => void;
};

const fmtFt = (v: number) => `${formatNumber(v, 0, 0)} ft`;

/**
 * The Merge Geometries panel: pick a landing point and a bottom hole (here or
 * on the map), preview the drilling order with its tangents, then create. Every
 * option, the name and the totals come from the server's preview.
 *
 * @param {MergeGeometriesPanelProps} props - Merge state and actions.
 * @return {JSX.Element | null} The panel.
 */
export const MergeGeometriesPanel: FC<MergeGeometriesPanelProps> = ({
  draft,
  landingOptions,
  bottomHoleOptions,
  landingKey,
  bottomHoleKey,
  chain,
  creating,
  onAssign,
  onCancel,
  onCreate,
}) => {
  if (!draft) return null;
  const loading = draft.loading === true;
  const loader = (
    <InlineLoading className={style.mergeLabelLoader} status="active" />
  );
  const landing = landingOptions.find((o) => o.key === landingKey) ?? null;
  const bottomHole =
    bottomHoleOptions.find((o) => o.key === bottomHoleKey) ?? null;

  return (
    <section className={style.mergePanel} aria-label="Merge Geometries">
      <div className={style.mergeField}>
        <span className={style.editSectionLabel}>Property ID</span>
        <span className={style.compositeName} title={draft.preview?.name}>
          {draft.preview?.name ?? '—'}
        </span>
      </div>
      <p className={style.railHint}>
        Click ends on the map — landing point first, then bottom hole — or
        choose them below.
      </p>
      <Dropdown
        id="merge-landing-point"
        size="sm"
        titleText={
          <span className={style.mergeLabel}>
            Landing point
            {loading && !draft.landing && loader}
          </span>
        }
        label="Choose an LTD end"
        disabled={loading}
        items={landingOptions}
        itemToString={(o: MergeEndOption | null) => o?.label ?? ''}
        selectedItem={landing}
        onChange={({ selectedItem }: { selectedItem: MergeEndOption | null }) =>
          onAssign('landing', selectedItem?.key ?? null)
        }
      />
      <Dropdown
        id="merge-bottom-hole"
        size="sm"
        titleText={
          <span className={style.mergeLabel}>
            Bottom hole
            {loading && draft.landing && loader}
          </span>
        }
        label={landing ? 'Choose an end on another LTD' : 'Choose an LTD end'}
        disabled={loading || !landing || bottomHoleOptions.length === 0}
        items={bottomHoleOptions}
        itemToString={(o: MergeEndOption | null) => o?.label ?? ''}
        selectedItem={bottomHole}
        onChange={({ selectedItem }: { selectedItem: MergeEndOption | null }) =>
          onAssign('bottomHole', selectedItem?.key ?? null)
        }
      />
      {chain ? (
        <>
          <StructuredListWrapper isCondensed aria-label="Drilling order">
            <StructuredListBody>
              {chain.rows.map((row, i) => (
                <Fragment key={row.slotId}>
                  {i > 0 && (
                    <StructuredListRow>
                      <StructuredListCell className={style.railHint}>
                        Tangent, not perforated
                      </StructuredListCell>
                      <StructuredListCell className={style.railHint}>
                        {row.tangentInFt === null
                          ? '—'
                          : fmtFt(row.tangentInFt)}
                      </StructuredListCell>
                    </StructuredListRow>
                  )}
                  <StructuredListRow>
                    <StructuredListCell>
                      <span className={style.editSectionLabel}>
                        {i === 0
                          ? 'Landing'
                          : i === chain.rows.length - 1
                            ? 'Bottom hole'
                            : `Segment ${i + 1}`}
                      </span>{' '}
                      {row.label}
                    </StructuredListCell>
                    <StructuredListCell>
                      {fmtFt(row.lengthFt)}
                    </StructuredListCell>
                  </StructuredListRow>
                </Fragment>
              ))}
            </StructuredListBody>
          </StructuredListWrapper>
          <dl className={style.mergeTotals}>
            <div>
              <dt>Producing</dt>
              <dd>{fmtFt(chain.producingLengthFt)}</dd>
            </div>
            <div>
              <dt>Tangents</dt>
              <dd>{fmtFt(chain.tangentLengthFt)}</dd>
            </div>
          </dl>
        </>
      ) : (
        <p className={style.railHint}>
          Set a landing point and a bottom hole. The remaining ends are joined
          to their nearest free neighbour with straight tangents; each LTD keeps
          its own row and producing length.
        </p>
      )}
      {draft.error && (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          title={draft.error}
        />
      )}
      <div className={style.mergeActions}>
        <Button kind="secondary" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          kind="primary"
          size="sm"
          disabled={!chain || loading || creating}
          onClick={onCreate}
        >
          Create composite LTD
        </Button>
      </div>
    </section>
  );
};
